import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useWhales } from '../lib/useWhales.js';
import { SceneIdeas } from '../components/SceneIdeas.js';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { FreeReferencePicker, type FreeRef } from '../components/FreeReferencePicker.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AmplifyResponse, GenerationMode, StylePreset, Whale } from '@gilding/shared';
import { MODE_CREDITS } from '@gilding/shared';
import { api } from '../api/client.js';
import { StylePicker } from '../components/StylePicker.js';
import { WhalePicker } from '../components/WhalePicker.js';
import { Reveal } from '../components/Reveal.js';
import { GildingCeremony } from '../components/GildingCeremony.js';
import { GildButton } from '../components/GildButton.js';
import { ConnectButton } from '../components/ConnectButton.js';
import { GildedImage } from '../components/GildedImage.js';
import { GildUnveil } from '../components/GildUnveil.js';
import { GildedBrief } from '../components/GildedBrief.js';
import { useConnect } from '../web3/useConnect.js';
import { NewGenerationScrub } from '../components/NewGenerationScrub.js';
import { useReducedMotion } from '../lib/useReducedMotion.js';
import { cn } from '../lib/classnames.js';

type FreeResult = Awaited<ReturnType<typeof api.generateFree>>;

type AspectRatio = '1:1' | '3:2' | '2:3' | '16:9' | '9:16';

const SKETCH_MAX = 600;

/**
 * Curated starting points — short, evocative chip labels that fill the field
 * with a full one-liner and switch to a fitting look, so a blank box never
 * stalls a first-timer. A deep pool spanning every style; a fresh handful is
 * drawn at random on each visit (see `pickRandom` + SketchSection) so the page
 * never feels like it offers the same six ideas every time.
 */
const SKETCH_EXAMPLES: ReadonlyArray<{ label: string; text: string; style: StylePreset }> = [
  // ---- photoreal-cinematic ----
  { label: 'Neon ramen, midnight', text: 'A neon-soaked ramen stall in the rain, Tokyo at midnight', style: 'photoreal-cinematic' },
  { label: 'Storm chaser', text: 'A storm chaser silhouetted against a wall of tornado cloud at golden hour', style: 'photoreal-cinematic' },
  { label: 'Diver and whale', text: 'A free-diver drifting beside a sleeping humpback in clear blue water', style: 'photoreal-cinematic' },
  { label: 'Desert highway', text: 'A lone car on an empty desert highway under a lightning storm', style: 'photoreal-cinematic' },
  { label: 'Greenhouse dawn', text: 'Sunrise breaking through a fogged Victorian greenhouse full of ferns', style: 'photoreal-cinematic' },
  { label: "Boxer's corner", text: 'A weary boxer slumped in the corner between rounds, sweat and steam', style: 'photoreal-cinematic' },
  { label: 'Arctic research', text: 'A red research tent glowing on an endless Antarctic ice sheet at dusk', style: 'photoreal-cinematic' },
  { label: 'Subway cellist', text: 'A cellist playing alone on an empty subway platform at 3am', style: 'photoreal-cinematic' },
  { label: 'Rooftop bees', text: 'A beekeeper tending hives on a Brooklyn rooftop at sunset', style: 'photoreal-cinematic' },
  { label: 'Flooded street', text: 'A child in a yellow raincoat wading down a flooded city street', style: 'photoreal-cinematic' },
  { label: 'Route 66 diner', text: 'A chrome diner glowing in the rain off a deserted Route 66', style: 'photoreal-cinematic' },
  { label: 'Temple monk', text: 'A monk sweeping snow from temple steps high in the misty mountains', style: 'photoreal-cinematic' },
  { label: 'Bangkok market', text: 'Steam and lanterns over a crowded Bangkok night market', style: 'photoreal-cinematic' },
  { label: 'Lighthouse storm', text: 'A lighthouse battered by waves under a bruised purple sky', style: 'photoreal-cinematic' },
  { label: 'Spacewalk', text: 'An astronaut tethered outside a station, Earth filling the visor', style: 'photoreal-cinematic' },
  { label: 'Old fisherman', text: 'A weathered fisherman mending nets at dawn on a stone harbor', style: 'photoreal-cinematic' },
  { label: 'Blossom train', text: 'A train sliding past cherry blossoms in full bloom, petals on the wind', style: 'photoreal-cinematic' },
  { label: 'Library ladder', text: 'A reader on a sliding ladder in a vast candlelit library', style: 'photoreal-cinematic' },
  { label: 'Foggy pier', text: 'A couple under one umbrella at the end of a fog-wrapped pier', style: 'photoreal-cinematic' },
  { label: 'Pit stop', text: 'A blur of mechanics swarming a race car in the pit lane', style: 'photoreal-cinematic' },
  { label: 'Glacier crossing', text: 'A tiny hiker crossing a vast blue glacier crevasse field', style: 'photoreal-cinematic' },
  { label: 'Jazz cellar', text: 'A trumpet solo lit by a single red bulb in a smoky jazz cellar', style: 'photoreal-cinematic' },
  { label: 'Vineyard harvest', text: 'Workers picking grapes in golden Tuscan light at harvest', style: 'photoreal-cinematic' },
  { label: 'Inside the barrel', text: 'A surfer deep inside a glassy turquoise barrel wave', style: 'photoreal-cinematic' },
  { label: 'Overgrown mall', text: 'Sunlight pouring through the broken roof of an overgrown shopping mall', style: 'photoreal-cinematic' },
  { label: 'Falconer', text: 'A falconer on a windswept moor as the hawk returns to the glove', style: 'photoreal-cinematic' },

  // ---- painterly ----
  { label: 'Lighthouse keeper', text: 'A lone lighthouse keeper reading by candlelight, oil painting', style: 'painterly' },
  { label: 'Harvest moon', text: 'A wheat field rolling under an enormous orange harvest moon', style: 'painterly' },
  { label: 'Mountain tea house', text: 'A quiet tea house overlooking a misty mountain garden', style: 'painterly' },
  { label: 'Clipper in the swell', text: 'A clipper ship riding a towering storm swell, romantic oil', style: 'painterly' },
  { label: 'Orchard picnic', text: 'A family picnic beneath blossoming apple trees, impressionist', style: 'painterly' },
  { label: 'Venetian dusk', text: 'Gondolas drifting a Venetian canal at golden dusk', style: 'painterly' },
  { label: 'Snowfall village', text: 'A warm-lit village under heavy falling snow at twilight', style: 'painterly' },
  { label: 'Fox in snow', text: 'A red fox curled in fresh snow beneath bare birches', style: 'painterly' },
  { label: 'Paris flower cart', text: "A flower seller's cart overflowing in a sunlit Paris square", style: 'painterly' },
  { label: 'Rainy window', text: 'A woman reading by a rain-streaked window in soft afternoon light', style: 'painterly' },
  { label: 'Autumn bridge', text: 'A wooden bridge over a river ablaze with autumn color', style: 'painterly' },
  { label: "Shepherd's hill", text: 'A shepherd and flock on a green hill under sweeping clouds', style: 'painterly' },
  { label: 'Candle still life', text: 'A still life of fruit, wine, and a guttering candle, Dutch master', style: 'painterly' },
  { label: 'Lavender rows', text: 'Endless lavender rows stretching to a hilltop villa', style: 'painterly' },
  { label: 'Cliffside watcher', text: "A fisherman's wife watching the sea from a windswept cliff", style: 'painterly' },
  { label: 'Seaside kites', text: 'Children flying kites on a blustery seaside afternoon', style: 'painterly' },
  { label: 'Old bookshop', text: 'The warm clutter of a tiny old bookshop at dusk', style: 'painterly' },
  { label: 'Alpine mirror', text: 'A mirror-still alpine lake reflecting snow peaks at dawn', style: 'painterly' },
  { label: 'Horse in the rain', text: 'A wild horse galloping across a storm-lashed moor', style: 'painterly' },
  { label: 'Garden gate', text: 'An overgrown garden gate spilling roses into a country lane', style: 'painterly' },
  { label: 'Moonlit wolves', text: 'A wolf pack crossing a frozen moonlit lake', style: 'painterly' },
  { label: 'Backstage ballerina', text: 'A ballerina lacing her shoes in warm backstage light, Degas-like', style: 'painterly' },

  // ---- 3d-render ----
  { label: 'Sunken cathedral', text: 'Bioluminescent jellyfish drifting through a sunken cathedral', style: '3d-render' },
  { label: 'Floating islands', text: 'A chain of floating islands linked by waterfalls and rope bridges', style: '3d-render' },
  { label: 'Cozy treehouse', text: 'A glowing treehouse cross-section, isometric, with tiny rooms', style: '3d-render' },
  { label: 'Robot gardener', text: 'A small rusted robot watering a rooftop garden, stylized 3D', style: '3d-render' },
  { label: 'Crystal cavern', text: 'A glowing crystal cavern with an underground river', style: '3d-render' },
  { label: 'Toy spaceship', text: 'A chunky toy spaceship parked on a candy-colored moon', style: '3d-render' },
  { label: 'City in a dome', text: 'A miniature city in a glass dome on a desk, tilt-shift 3D', style: '3d-render' },
  { label: 'Mushroom cottage', text: 'A cutaway mushroom cottage with a snail mailbox', style: '3d-render' },
  { label: 'Mars outpost', text: 'A lonely fuel outpost on a Martian desert highway', style: '3d-render' },
  { label: 'Underwater lab', text: 'A domed underwater research lab ringed by curious fish', style: '3d-render' },
  { label: 'Clockwork heart', text: 'An exploded view of a brass clockwork heart, studio render', style: '3d-render' },
  { label: 'Candy factory', text: 'A whimsical candy factory of pipes and gumball tanks', style: '3d-render' },
  { label: 'Diner in orbit', text: 'A retro chrome diner floating in orbit, neon sign lit', style: '3d-render' },
  { label: 'Ceramic frog', text: 'A glossy ceramic frog on a lily pad, soft studio light', style: '3d-render' },
  { label: 'Lighthouse isle', text: 'A tiny isometric lighthouse island with a spiral path', style: '3d-render' },
  { label: 'Mech bay', text: 'A worn mech kneeling in a cluttered repair bay', style: '3d-render' },
  { label: 'Geodesic greenhouse', text: 'A geodesic greenhouse dome on a snowy ridge', style: '3d-render' },
  { label: 'Toy train tunnel', text: 'A looping toy train through a tiny mountain tunnel', style: '3d-render' },
  { label: 'Stilt market', text: 'A floating market of stilt stalls over teal water, low-poly', style: '3d-render' },
  { label: 'Dragon egg', text: 'A cracked dragon egg glowing on a velvet cushion, product render', style: '3d-render' },
  { label: 'Arcade cabinet', text: 'A lovingly detailed retro arcade cabinet, neon glow', style: '3d-render' },
  { label: 'Cloud castle', text: 'A pastel castle perched on a single fluffy cloud', style: '3d-render' },

  // ---- editorial-illustration ----
  { label: 'Fox in a suit', text: 'A fox in a tiny tailored suit, studio portrait', style: 'editorial-illustration' },
  { label: 'Coffee ritual', text: 'A flat-color spread on the perfect morning coffee ritual', style: 'editorial-illustration' },
  { label: 'Cat astronomer', text: 'A cat charting constellations from a tower window', style: 'editorial-illustration' },
  { label: 'Reading nook', text: 'A cozy reading nook stuffed with books and a sleeping cat', style: 'editorial-illustration' },
  { label: 'Bicycle city', text: 'A cheerful crowd of cyclists weaving through a sunny city', style: 'editorial-illustration' },
  { label: 'Umbrella commute', text: 'Commuters under a forest of umbrellas, limited palette', style: 'editorial-illustration' },
  { label: 'Garden tools', text: 'A tidy flat-lay of gardening tools and seed packets', style: 'editorial-illustration' },
  { label: 'Owl librarian', text: 'An owl stamping books at a tiny forest library desk', style: 'editorial-illustration' },
  { label: 'Picnic from above', text: 'An overhead picnic spread on a checkered blanket', style: 'editorial-illustration' },
  { label: 'Bakery window', text: 'A charming bakery window crowded with pastries', style: 'editorial-illustration' },
  { label: 'Map on the ledge', text: 'A hiker unfolding a giant map on a mountain ledge', style: 'editorial-illustration' },
  { label: 'Whale and rowboat', text: 'A friendly whale lifting a tiny rowboat, gentle editorial', style: 'editorial-illustration' },
  { label: 'City rooftops', text: 'A patchwork of city rooftops with cats and laundry lines', style: 'editorial-illustration' },
  { label: 'Balcony tea', text: 'Two friends sharing tea on a balcony full of plants', style: 'editorial-illustration' },
  { label: 'Bookstore cat', text: 'A bookstore cat asleep among toppling paperbacks', style: 'editorial-illustration' },
  { label: 'Market day', text: 'A bustling farmers market in flat warm color', style: 'editorial-illustration' },
  { label: 'Night train window', text: 'A sleepy passenger watching the night slide past a train window', style: 'editorial-illustration' },
  { label: 'Plant parent', text: 'A proud plant parent lost in a jungle of houseplants', style: 'editorial-illustration' },
  { label: 'Fox under a mushroom', text: 'A fox sheltering under a mushroom in the rain', style: 'editorial-illustration' },
  { label: 'Letter writer', text: 'Someone writing letters at a sunlit, cluttered desk', style: 'editorial-illustration' },
  { label: 'Bird watcher', text: 'A bird watcher and a curious flock, soft editorial palette', style: 'editorial-illustration' },
  { label: 'Corgi bath', text: 'A corgi mid-shake in a tiny tub of bubbles', style: 'editorial-illustration' },

  // ---- logo-poster ----
  { label: 'Poster: Saturn', text: 'A retro travel poster for a city on the rings of Saturn', style: 'logo-poster' },
  { label: 'Coffee roaster', text: 'A clean logo and poster for a mountain coffee roaster', style: 'logo-poster' },
  { label: 'Jazz festival', text: 'A bold mid-century poster for a downtown jazz festival', style: 'logo-poster' },
  { label: 'National park', text: 'A vintage WPA-style poster for a redwood national park', style: 'logo-poster' },
  { label: 'Surf shop badge', text: 'A sun-bleached surf shop badge with a breaking wave', style: 'logo-poster' },
  { label: 'Ramen bar neon', text: 'A neon-typographic poster for a late-night ramen bar', style: 'logo-poster' },
  { label: 'Bike race', text: 'A dynamic poster for a city bike race in bold type', style: 'logo-poster' },
  { label: 'Botanical print', text: 'An elegant botanical specimen poster with serif labels', style: 'logo-poster' },
  { label: 'Visit the Moon', text: 'A "visit the Moon" tourism poster, art deco', style: 'logo-poster' },
  { label: 'Record label', text: 'A minimalist record-label mark for a vinyl press', style: 'logo-poster' },
  { label: 'Film noir', text: 'A high-contrast film-noir movie poster with big type', style: 'logo-poster' },
  { label: 'Market poster', text: 'A cheerful Saturday farmers-market poster', style: 'logo-poster' },
  { label: 'Climbing gym', text: 'A bold geometric logo for an indoor climbing gym', style: 'logo-poster' },
  { label: 'Tea wordmark', text: 'A refined tea-brand wordmark with a leaf motif', style: 'logo-poster' },
  { label: 'Sci-fi premiere', text: 'A retro-future poster for a sci-fi film premiere', style: 'logo-poster' },
  { label: 'City marathon', text: 'A clean, energetic poster for a city marathon', style: 'logo-poster' },
  { label: 'Corner bakery', text: 'A warm hand-lettered logo for a corner bakery', style: 'logo-poster' },
  { label: 'Observatory deco', text: 'An art-deco poster for a hilltop observatory', style: 'logo-poster' },
  { label: 'Skate badge', text: 'A gritty skate-brand badge with bold lettering', style: 'logo-poster' },
  { label: 'Wine label', text: 'An elegant minimalist wine label with gold accents', style: 'logo-poster' },
  { label: 'Gig poster', text: 'A bold gig poster for an underground music venue', style: 'logo-poster' },
  { label: 'See it by rail', text: 'A nostalgic "see the coast by rail" travel poster', style: 'logo-poster' },

  // ---- anime-leaning ----
  { label: 'Samurai cat', text: 'A samurai cat duels a thunder dragon at dawn', style: 'anime-leaning' },
  { label: 'Sky pirates', text: 'A girl leaping between sky-pirate airships in the clouds', style: 'anime-leaning' },
  { label: 'Waking the book', text: 'A young mage waking a floating book in a vast library', style: 'anime-leaning' },
  { label: 'Rooftop nap', text: 'A tired student napping on a school rooftop at sunset', style: 'anime-leaning' },
  { label: 'Spirit festival', text: 'A lantern-lit spirit festival along a river at night', style: 'anime-leaning' },
  { label: 'Mecha in the rain', text: 'A pilot sprinting toward a waking mecha in the rain', style: 'anime-leaning' },
  { label: 'Rainy bus stop', text: 'A girl and a giant forest spirit sharing a bus stop in the rain', style: 'anime-leaning' },
  { label: 'Cliff at sunrise', text: 'A swordsman facing the sunrise on a cliff of cherry trees', style: 'anime-leaning' },
  { label: 'Snowy ramen alley', text: 'A cozy ramen shop steaming on a snowy alley at night', style: 'anime-leaning' },
  { label: 'Witch at dawn', text: 'A young witch coasting over rooftops on her broom at dawn', style: 'anime-leaning' },
  { label: 'Train of spirits', text: 'Quiet spirits riding a midnight train through the stars', style: 'anime-leaning' },
  { label: 'Forest dragon', text: 'A child napping against a huge, gentle forest dragon', style: 'anime-leaning' },
  { label: 'Summer fireworks', text: 'Two friends watching fireworks in summer yukata', style: 'anime-leaning' },
  { label: 'Ghost cat path', text: 'A glowing ghost cat leading the way down a temple path', style: 'anime-leaning' },
  { label: 'Rusty robot umbrella', text: 'A small girl and a rusty robot sharing an umbrella', style: 'anime-leaning' },
  { label: 'Ocean train', text: 'A train racing along tracks that vanish into the sea', style: 'anime-leaning' },
  { label: 'Glider valley', text: 'A glider riding wind currents over a green valley', style: 'anime-leaning' },
  { label: 'Transformation', text: 'A magical girl mid-transformation over a neon skyline', style: 'anime-leaning' },
  { label: 'Tea spirits', text: 'A tea house served by tiny floating spirits', style: 'anime-leaning' },
  { label: 'Snow fox shrine', text: 'A white fox spirit guarding a snowbound shrine', style: 'anime-leaning' },
  { label: 'Fishing for stars', text: 'A boy fishing for stars from a floating dock at night', style: 'anime-leaning' },
  { label: 'Spring confession', text: 'A shy confession under one umbrella in the spring rain', style: 'anime-leaning' },

  // ---- experimental ----
  { label: 'Liquid chrome', text: 'A face dissolving into flowing liquid chrome and light', style: 'experimental' },
  { label: 'Glitch garden', text: 'A garden where flowers glitch into pixels and static', style: 'experimental' },
  { label: 'Paper galaxy', text: 'A galaxy folded entirely from torn paper and gold leaf', style: 'experimental' },
  { label: 'Ink horse', text: 'Black ink blooming into a horse galloping through water', style: 'experimental' },
  { label: 'Neon anatomy', text: 'A neon wireframe heart pulsing in total darkness', style: 'experimental' },
  { label: 'Melting clock', text: 'A melting clock dripping into a desert of mirrors', style: 'experimental' },
  { label: 'Fractal forest', text: 'An infinite fractal forest in iridescent oil-slick color', style: 'experimental' },
  { label: 'Data storm', text: 'A figure walking into a storm of floating data shards', style: 'experimental' },
  { label: 'Smoke dancer', text: 'A dancer formed entirely of colored smoke, mid-leap', style: 'experimental' },
  { label: 'Crystal bloom', text: 'A flower growing crystals in fast-forward, macro abstraction', style: 'experimental' },
  { label: 'Wolf double exposure', text: 'A double exposure of a wolf and a snowy pine forest', style: 'experimental' },
  { label: 'Thread portrait', text: 'A portrait stitched from thousands of glowing threads', style: 'experimental' },
  { label: 'Gravity house', text: 'An Escher house where each room has its own gravity', style: 'experimental' },
  { label: 'Waveform skyline', text: 'A city skyline drawn entirely from sound waveforms', style: 'experimental' },
  { label: 'Mirror desert', text: 'A lone figure in a desert of standing mirrors at dusk', style: 'experimental' },
  { label: 'Paint explosion', text: 'A hummingbird frozen in an explosion of wet paint', style: 'experimental' },
  { label: 'Origami beast', text: 'A massive origami beast unfolding in a white void', style: 'experimental' },
  { label: 'Light painting', text: 'A long-exposure light painting of a running figure', style: 'experimental' },
  { label: 'Molten upward', text: 'Molten color pouring upward against gravity, abstract', style: 'experimental' },
  { label: 'Static ghost', text: 'A ghost flickering between TV static and stained glass', style: 'experimental' },
  { label: 'Kaleidoscope city', text: 'A city reflected into a living kaleidoscope', style: 'experimental' },
  { label: 'Frozen splash', text: 'A frozen milk-crown splash sculpture, hyper macro', style: 'experimental' },
  // ---- wildly cross-genre (breaks the tasteful-cinematic monotony) ----
  { label: 'Dragon hoard', text: 'A dragon dozing atop a mountain of gold in a vast cavern', style: 'painterly' },
  { label: 'Mech cockpit', text: 'A pilot in a giant mech cockpit lit by a hundred switches', style: 'anime-leaning' },
  { label: 'Retro rocket', text: 'A chrome 1950s rocket landing on a candy-red alien plain', style: 'editorial-illustration' },
  { label: 'Swamp horror', text: 'Something enormous stirring under a fog-choked bayou at night', style: 'photoreal-cinematic' },
  { label: 'Tea with ghost', text: 'A polite little ghost pouring tea in a cozy cluttered kitchen', style: 'anime-leaning' },
  { label: 'Pirate leap', text: 'A pirate swinging between two ships as cannons flash', style: 'painterly' },
  { label: 'Space diner', text: 'A neon diner floating at a zero-gravity spaceport', style: 'editorial-illustration' },
  { label: 'Wizard laundry', text: 'A wizard mending an enchanted clockwork owl in a washhouse', style: 'painterly' },
  { label: 'Kaiju harbor', text: 'A colossal beast rising over a tiny harbor as sirens wail', style: 'anime-leaning' },
  { label: 'Desert duel', text: 'Two gunslingers frozen at high noon on a dusty main street', style: 'photoreal-cinematic' },
  { label: 'Mushroom village', text: 'A snug village built inside giant glowing mushrooms', style: 'painterly' },
  { label: 'Robot repair', text: 'A tiny robot fixing a smaller robot on a workshop bench', style: '3d-render' },
  { label: 'Cloud whale', text: 'A whale made of clouds drifting over a floating sky-island', style: 'painterly' },
  { label: 'Detective rain', text: 'A trench-coat detective under a flickering sign in the rain', style: 'photoreal-cinematic' },
  { label: 'Candy factory', text: 'A whimsical candy factory of pipes, gears, and gumdrops', style: '3d-render' },
  { label: 'Deep-sea lab', text: 'A cracked porthole in a deep-sea lab, eyes glinting outside', style: 'photoreal-cinematic' },
  { label: 'Samurai duel', text: 'A samurai mid-strike under falling maple leaves at dusk', style: 'anime-leaning' },
  { label: 'Circus of moths', text: 'A moonlit circus tent where the acrobats are giant moths', style: 'editorial-illustration' },
  { label: 'Sky pirates', text: 'An airship crew scrambling as they dodge a lightning storm', style: 'anime-leaning' },
  { label: 'Cozy dragon café', text: 'A tiny dragon curled by the hearth of a snowbound café', style: 'painterly' },
  { label: 'Vaporwave shrine', text: 'A pink marble shrine among palm trees in endless dusk', style: 'experimental' },
  { label: 'Dino crossing', text: 'A herd of longnecks wading a misty prehistoric river', style: 'photoreal-cinematic' },
  { label: 'Golem garden', text: 'A mossy stone golem quietly tending a walled flower garden', style: 'painterly' },
  { label: 'Arcade champion', text: 'A kid raising both fists at a glowing 80s arcade cabinet', style: 'editorial-illustration' },
  { label: 'Witch market', text: 'A bustling night bazaar of potions, charms, and floating lamps', style: 'anime-leaning' },
  { label: 'Astronaut picnic', text: 'An astronaut having a quiet picnic on a small green moon', style: '3d-render' },
  { label: 'Ice palace', text: 'A throne room carved entirely from blue glacier ice', style: 'painterly' },
  { label: 'Noir jazz cat', text: 'A cool cat on saxophone in a smoky underground club', style: 'editorial-illustration' },
  { label: 'Clockwork heart', text: 'An open brass chest revealing a ticking clockwork heart', style: '3d-render' },
  { label: 'Festival kite', text: 'A giant koi kite towing a laughing child over rooftops', style: 'anime-leaning' },
];

/** How many example chips to surface at once (a fresh random draw per visit). */
const EXAMPLE_COUNT = 6;

/** Pick `n` distinct items from `pool` at random (returns all if pool ≤ n). */
function pickRandom<T>(pool: ReadonlyArray<T>, n: number): T[] {
  if (pool.length <= n) return [...pool];
  const used = new Set<number>();
  const out: T[] = [];
  while (out.length < n) {
    const i = Math.floor(Math.random() * pool.length);
    if (used.has(i)) continue;
    used.add(i);
    out.push(pool[i]!);
  }
  return out;
}

/** Credits are surfaced to users as "leaves" — never "cr" or "credits". */
function leafCost(n: number): string {
  return `${n} ${n === 1 ? 'leaf' : 'leaves'}`;
}

/**
 * A gild request that failed at the *transport* layer, not the API layer.
 * Mobile browsers tear down the in-flight /generate socket when the screen
 * sleeps or the user switches apps; fetch then rejects with a bare `TypeError`
 * ("Load failed" on WebKit, "Failed to fetch" on Chromium) and NO structured
 * error code — even though the Worker finished the gild and saved it. Real API
 * failures (insufficient_credits, moderation, 5xx) always carry a `code` from
 * jsonFetch, so the absence of a code is the tell. We recover from these
 * instead of showing a false failure.
 */
function isTransientNetworkError(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  if ((e as { code?: string }).code) return false; // structured API error — real
  const m = e.message.toLowerCase();
  return (
    e.name === 'AbortError' ||
    e.name === 'TypeError' ||
    m.includes('load failed') ||
    m.includes('failed to fetch') ||
    m.includes('networkerror') ||
    m.includes('network error') ||
    m.includes('network connection was lost') ||
    m.includes('cancelled') ||
    m.includes('canceled')
  );
}

const MODE_TITLE: Record<GenerationMode, string> = {
  single: 'One gilding',
  double: 'Two variations',
  '4up': 'Four variations',
  battle: 'Battle: three models',
};

const MODE_BLURB: Record<GenerationMode, string> = {
  single: 'One finished image. The default.',
  double: 'Two takes on your brief. Keep the better one.',
  '4up': 'Four variations of one brief, side by side.',
  battle: 'Three different engines interpret the same brief.',
};

const ASPECT_RATIOS: ReadonlyArray<{ id: AspectRatio; w: number; h: number; caption: string }> = [
  { id: '1:1', w: 28, h: 28, caption: 'square' },
  { id: '3:2', w: 30, h: 20, caption: 'classic' },
  { id: '2:3', w: 20, h: 30, caption: 'portrait' },
  { id: '16:9', w: 32, h: 18, caption: 'widescreen' },
  { id: '9:16', w: 18, h: 32, caption: 'phone' },
];

export function NewGeneration() {
  const [searchParams] = useSearchParams();
  const remixSourceId = searchParams.get('remix');
  const initialStyle = (searchParams.get('style') as StylePreset | null) ?? 'photoreal-cinematic';
  const initialAspect = (searchParams.get('aspect') as AspectRatio | null) ?? '1:1';

  // A share link can prefill the sketch: /new?prompt=…&style=…&aspect=…
  const [sketch, setSketch] = useState<string>(() => searchParams.get('prompt') ?? '');
  const [stylePreset, setStylePreset] = useState<StylePreset>(initialStyle);
  const [mode, setMode] = useState<GenerationMode>('single');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(initialAspect);
  const [whale, setWhale] = useState<Whale | null>(null);
  // A character that isn't in the wallet — looked up by whale number or uploaded
  // (another member's whale, any NFT, any character). Mutually exclusive with the
  // owned-whale pick above; setting one clears the other.
  const [uploadedRef, setUploadedRef] = useState<FreeRef | null>(null);
  // Shared cache key with WhalePicker — used only to decide whether the picker
  // block renders at all. A wallet with no whales drops the section entirely
  // (no header, no note, no empty Reveal slot); a stand-in steps in silently.
  // Prefetched at page mount (this runs before the picker sheet opens), and
  // kept for the session so the pod is warm by the time the user browses it.
  const whalesQuery = useWhales();
  const showWhalePicker =
    whalesQuery.isLoading || whalesQuery.isError || (whalesQuery.data?.whales.length ?? 0) > 0;
  // Bumped when "Surprise me" lands a suggestion — flashes the field underline.
  const [suggestFlash, setSuggestFlash] = useState(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Remix source preview — image + style/aspect we copied into local state.
  const remixSourceQuery = useQuery({
    queryKey: ['gen', remixSourceId],
    queryFn: () => api.generation(remixSourceId!),
    enabled: !!remixSourceId,
    retry: false,
  });
  const remixSourceThumb = useMemo(() => {
    const d = remixSourceQuery.data;
    if (!d) return null;
    // Always show the CHOSEN cover — the image people saw in the feed — not the
    // first variant. The card already carries the cover thumbnail; the full
    // record carries the cover index.
    if (d.kind === 'card') return d.card.thumbnail;
    if (d.kind === 'full') {
      return d.result.images[d.result.coverIndex ?? 0]?.url ?? d.result.images[0]?.url ?? null;
    }
    return null;
  }, [remixSourceQuery.data]);

  // Backgrounded-gild recovery. The synchronous /generate socket dies when a
  // phone sleeps or the user app-switches mid-gild, so the request rejects with
  // a bare network error even though the Worker finished and saved the piece.
  // `recovering` = we're polling the gallery for that saved gild; `recoverHint`
  // = we gave up polling and point the user at their gallery (a gentle message,
  // never the red failure box). A snapshot of the pre-gild gallery ids lets us
  // pick out the freshly-saved one without landing on a stale gild.
  const [recovering, setRecovering] = useState(false);
  const [recoverHint, setRecoverHint] = useState(false);
  const gildStartRef = useRef<{ at: number; known: Set<string> }>({ at: 0, known: new Set() });

  // The in-flight background gild. Once `/generate/start` hands back an id, the
  // actual render runs server-side in a durable job (see GenerationJobDO), so we
  // just poll its status and land on the result whenever it finishes — even if
  // the phone slept through the whole render. `jobBrief` is the amplified prompt
  // returned up-front, shown as the craft while the pixels come in. `jobStartedAt`
  // bounds the wait so a wedged job eventually points the user at their gallery.
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobBrief, setJobBrief] = useState<AmplifyResponse | null>(null);
  const [jobError, setJobError] = useState<string | null>(null);
  const jobStartedAtRef = useRef<number>(0);

  const recoverGild = async (snap: { at: number; known: Set<string> }): Promise<void> => {
    setRecovering(true);
    // Poll for ~50s (12 tries). The completed gild lands in the gallery within
    // a few seconds of the socket dropping; give it a moment, then look.
    for (let i = 0; i < 12; i++) {
      await new Promise((r) => setTimeout(r, i === 0 ? 1500 : 4000));
      let mine: Awaited<ReturnType<typeof api.myGenerations>> | null = null;
      try {
        mine = await api.myGenerations(8, 0);
      } catch {
        continue; // network still flaky on return — keep trying
      }
      const fresh = mine.items.find((g) => {
        // Prefer the id snapshot: any gild not present before we fired is ours.
        if (snap.known.size > 0) return !snap.known.has(g.id);
        // Snapshot never landed (backgrounded instantly) — fall back to time.
        const t = g.createdAt ? new Date(g.createdAt).getTime() : 0;
        return t >= snap.at - 60_000;
      });
      if (fresh) {
        setRecovering(false);
        await queryClient.invalidateQueries({ queryKey: ['me'] });
        navigate(`/result/${fresh.id}`, { state: fresh });
        return;
      }
    }
    // Couldn't confirm it — send them to the gallery rather than alarm them.
    setRecovering(false);
    setRecoverHint(true);
  };

  // Durable gild. `/generate/start` runs only the fast, synchronous pre-flight
  // (moderation, credits, amplify — so those errors still surface immediately)
  // and returns an id; the actual render then runs to completion server-side in
  // a durable alarm-driven job. We poll its status and land on the result the
  // moment it finishes, so a phone that sleeps or app-switches through the whole
  // render simply picks the finished piece up on return — no false failure, no
  // lost gild. The only window still exposed to a dropped socket is the ~3s
  // start request itself; if THAT is lost, onError falls back to the gallery
  // snapshot recovery (the job still ran, so the piece is there to find).
  const generateMut = useMutation({
    mutationFn: () =>
      api.startGenerate({
        sketch: remixSourceId ? '' : sketch,
        stylePreset,
        mode,
        aspectRatio,
        modelOverride: null,
        referenceImageUrl: whale?.imageUrl ?? uploadedRef?.url ?? null,
        hasReference: whale !== null || uploadedRef !== null,
        remixSourceId: remixSourceId ?? null,
        fixSourceImageUrl: null,
        fixInstruction: null,
        fixReferenceImageUrls: [],
        characterTokenId: whale?.tokenId ?? uploadedRef?.tokenId ?? null,
      }),
    onMutate: () => {
      setRecoverHint(false);
      setJobError(null);
      const at = Date.now();
      gildStartRef.current = { at, known: new Set() };
      // Snapshot the current gallery ids so the fallback recovery (used only if
      // the start request itself is lost) can spot the new gild. Fire-and-forget.
      void api
        .myGenerations(12, 0)
        .then((mine) => {
          if (gildStartRef.current.at === at) {
            gildStartRef.current.known = new Set(mine.items.map((g) => g.id));
          }
        })
        .catch(() => {});
    },
    onSuccess: (res) => {
      // Render is now running server-side; poll status until it lands.
      jobStartedAtRef.current = Date.now();
      setJobBrief(res.amplified);
      setJobId(res.id);
      try { if (meQuery.data?.address) localStorage.setItem(`gilding_job:${meQuery.data.address}`, JSON.stringify({ id: res.id, at: Date.now() })); } catch { /* account activity also tracks the job */ }
    },
    onError: (e) => {
      if (isTransientNetworkError(e)) void recoverGild(gildStartRef.current);
    },
  });

  // Poll the background gild's status while one is in flight. TanStack refetches
  // on window focus too, so returning to the tab checks immediately.
  const statusQuery = useQuery({
    queryKey: ['genstatus', jobId],
    queryFn: () => api.generationStatus(jobId!),
    enabled: jobId !== null,
    refetchInterval: (q) => {
      const s = q.state.data?.state;
      return s === 'done' || s === 'failed' ? false : 2500;
    },
    refetchIntervalInBackground: true,
    staleTime: 0,
    gcTime: 0,
  });

  // React to a terminal status: load + land on 'done', surface 'failed', and
  // give up gracefully if a job wedges (→ gentle gallery hint, never a red box).
  useEffect(() => {
    if (!jobId) return;
    const s = statusQuery.data?.state;
    if (s === 'done') {
      try { if (meQuery.data?.address) localStorage.removeItem(`gilding_job:${meQuery.data.address}`); } catch { /* optional browser persistence */ }
      let cancelled = false;
      void (async () => {
        try {
          const full = await api.generation(jobId);
          if (cancelled) return;
          await queryClient.invalidateQueries({ queryKey: ['me'] });
          navigate(`/result/${jobId}`, {
            state: full.kind === 'full' ? full.result : undefined,
          });
        } catch {
          if (!cancelled) navigate(`/result/${jobId}`);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    if (s === 'failed') {
      try { if (meQuery.data?.address) localStorage.removeItem(`gilding_job:${meQuery.data.address}`); } catch { /* optional browser persistence */ }
      setJobError(
        statusQuery.data?.message ??
          'Something didn’t take, and your leaves were not affected. Please try again.',
      );
      setJobId(null);
      setJobBrief(null);
      return undefined;
    }
    // Still processing/unknown — bound the wait so a wedged job doesn't spin
    // forever. The render itself is durable, so we point at the gallery.
    if (jobStartedAtRef.current && Date.now() - jobStartedAtRef.current > 180_000) {
      setJobId(null);
      setJobBrief(null);
      setRecoverHint(true);
    }
    return undefined;
  }, [jobId, statusQuery.data?.state, statusQuery.data?.message, navigate, queryClient]);

  // Surprise-me — Claude generates one fresh scene + preset based on the
  // currently-picked character. Fires fresh on every click (no cache).
  const recentIdeas = useRef<string[]>([]);
  const currentSuggestionContext = useRef('');
  currentSuggestionContext.current = `${whale?.imageUrl ?? uploadedRef?.url ?? ''}|${sketch}|${stylePreset}`;
  const requestedSuggestionContext = useRef('');
  const suggestMut = useMutation({
    onMutate: () => { requestedSuggestionContext.current = currentSuggestionContext.current; },
    mutationFn: () =>
      api.suggest({
        referenceImageUrl: whale?.imageUrl ?? uploadedRef?.url ?? null,
        recentSketches: recentIdeas.current,
      }),
    onSuccess: (result) => {
      recentIdeas.current = [result.sketch, ...recentIdeas.current].slice(0, 5);
      if (requestedSuggestionContext.current !== currentSuggestionContext.current) return;
      setSketch(result.sketch);
      setStylePreset(result.stylePreset);
      setSuggestFlash((t) => t + 1);
    },
  });

  // Auth state — signed-out visitors get the no-wallet free path.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 });
  const authed = meQuery.data?.authenticated === true;
  const isHolder = meQuery.data?.holder === true;
  useEffect(() => {
    const address = meQuery.data?.address;
    if (!address) return;
    try {
      const saved = JSON.parse(localStorage.getItem(`gilding_job:${address}`) ?? 'null');
      if (saved?.id && /^gen_[a-zA-Z0-9_-]+$/.test(saved.id) && Date.now() - saved.at < 86400000) {
        jobStartedAtRef.current = Date.now(); setJobId(saved.id);
      }
    } catch { /* invalid browser record */ }
  }, [meQuery.data?.address]);

  const [freeResult, setFreeResult] = useState<FreeResult | null>(null);
  // Optional character for the free gild — a Whacky Whale (by number) or an
  // uploaded image. When set, the free gild runs reference-based.
  const [freeRef, setFreeRef] = useState<FreeRef | null>(null);
  const generateFreeMut = useMutation({
    mutationFn: () =>
      api.generateFree({
        sketch: sketch.trim(),
        stylePreset,
        aspectRatio,
        referenceImageUrl: freeRef?.url ?? null,
      }),
    onSuccess: (r) => {
      setFreeResult(r);
      // Remember this free gilding's id so the moment the visitor connects a
      // wallet (here or anywhere else in the app) it gets saved to their
      // gallery just like any paid one. The claim itself lives in useConnect.
      try {
        window.localStorage.setItem('gilding_free_pending', r.id);
      } catch {
        /* storage blocked — claim simply won't happen; no harm */
      }
    },
  });
  // "Surprise me" for the no-wallet flow — same fresh-prompt roll as the signed-in
  // page, just without a character.
  const suggestFreeMut = useMutation({
    mutationFn: () => api.suggestFree(),
    onSuccess: (result) => {
      setSketch(result.sketch);
      setStylePreset(result.stylePreset);
      setSuggestFlash((t) => t + 1);
    },
  });
  const { connect, connecting, error: connectError } = useConnect();
  const onConnected = async (): Promise<void> => {
    const res = await connect();
    if (res.ok) {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate(res.holder ? '/dashboard' : '/early-access');
    }
  };
  // "Make this yours" — carry the free sketch + style + shape into the signed-in
  // form (as state, so it survives the auth flip on this same page), connect,
  // then drop the free view. The visitor lands on the real form with everything
  // ready, so the momentum from the free taste never breaks.
  const onMakeYours = async (): Promise<void> => {
    if (freeResult) {
      setSketch(freeResult.request.sketch);
      setStylePreset(freeResult.request.stylePreset as StylePreset);
      setAspectRatio(freeResult.request.aspectRatio as AspectRatio);
    }
    const res = await connect();
    if (res.ok) {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      // Their free gild is now claimed to the wallet. Holders drop into the full
      // form on /new; non-holders go to the early-access gate.
      if (res.holder) setFreeResult(null);
      else navigate('/early-access');
    }
  };

  const isRemix = !!remixSourceId;
  // A character is attached whether it's an owned whale or a looked-up/uploaded one.
  const isKontext = whale !== null || uploadedRef !== null;
  const characterName = whale?.name ?? uploadedRef?.label ?? null;

  // Hook used by the cinematic branch. MUST live above the early returns
  // below (Navigate for non-holders, signed-out free flow) — otherwise React
  // sees a different hook count between renders and throws "Rendered fewer
  // hooks than expected", which reads to the user as a black screen.
  const reduced = useReducedMotion();

  // `?preview=1` — a design-review override that forces the cinematic scrub
  // to render regardless of auth state. Safe in prod because the API still
  // enforces auth on writes (Gild-it 401s without a session); this only
  // decides which JSX branch renders, so a signed-out visitor can walk the
  // corridor on a phone without signing a message first.
  const isPreview = searchParams.get('preview') === '1';

  // A signed-in NON-holder can't use the full gild — send them to the gate.
  // Preview mode bypasses this too, since a design reviewer isn't a real
  // non-holder to be gated.
  if (authed && !isHolder && !isPreview) {
    return <Navigate to="/early-access" replace />;
  }

  // ---------- Signed out: the no-wallet free taste ----------
  // Preview mode skips this too — the reviewer wants the full six-beat
  // scrub, not the classic single-column free taste.
  if (!authed && !meQuery.isLoading && !isPreview) {
    if (freeResult) {
      return (
        <FreeResultView
          result={freeResult}
          connecting={connecting}
          connectError={connectError}
          onConnect={() => void onConnected()}
          onMakeYours={() => void onMakeYours()}
        />
      );
    }
    const freeErr: (Error & { code?: string }) | null = generateFreeMut.error;
    const freeCode = freeErr?.code ?? '';
    const freeUsed =
      freeCode === 'free_used' || freeCode === 'free_ip_limit' || freeCode === 'free_busy';
    const freeMsg =
      freeErr && !freeUsed
        ? freeCode === 'moderation_refused'
          ? freeErr.message
          : freeErr.message || 'Something didn’t take. Try again in a moment.'
        : null;
    const canGild = sketch.trim().length >= 3 && !generateFreeMut.isPending;
    return (
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-10 px-5 py-10 md:py-14">
        <Reveal as="header" className="flex flex-col gap-3">
          <span className="kicker">First one’s on us</span>
          <h1 className="heading-display">Bring a sketch.</h1>
          <p className="max-w-prose font-display italic text-lede text-ink-soft">
            One line, and Claude gilds it into a finished image. No wallet, no sign-up for your first.
          </p>
        </Reveal>

        <Reveal delay={70} className="flex flex-col gap-2">
          <SketchSection
            isKontext={false}
            whaleName={null}
            value={sketch}
            onChange={setSketch}
            max={SKETCH_MAX}
            onSurprise={() => suggestFreeMut.mutate()}
            surprising={suggestFreeMut.isPending}
            flashToken={suggestFlash}
            onExample={(text, style) => {
              setSketch(text);
              setStylePreset(style);
              setSuggestFlash((t) => t + 1);
            }}
          />
          {suggestFreeMut.isError && (
            <p className="font-display italic text-caption text-ink-subtle">
              {(suggestFreeMut.error as Error & { code?: string })?.code === 'free_suggest_limit'
                ? 'That’s a lot of surprises! Sign in to keep rolling.'
                : 'Couldn’t fetch a surprise just now. Try again.'}
            </p>
          )}
        </Reveal>

        <Reveal as="section" delay={60} className="flex flex-col gap-3">
          <h2 className="heading-section">Add a character <span className="text-ink-subtle">(optional)</span></h2>
          <p className="max-w-prose font-display italic text-caption text-ink-subtle">
            Gild a Whacky Whale by its number, or upload your own whale, NFT, or character to star in the scene.
          </p>
          <FreeReferencePicker value={freeRef} onChange={setFreeRef} />
        </Reveal>

        <Reveal as="section" delay={60} className="flex flex-col gap-4">
          <h2 className="heading-section">Pick the look</h2>
          <StylePicker value={stylePreset} onChange={setStylePreset} />
        </Reveal>

        <Reveal as="section" delay={60} className="flex flex-col gap-4">
          <h2 className="heading-section">What shape</h2>
          <AspectPicker value={aspectRatio} onChange={setAspectRatio} />
        </Reveal>

        {freeUsed && (
          <div className="flex flex-col gap-3 rounded-sm border border-accent/30 bg-accent/[0.05] px-4 py-4">
            <p className="heading-section text-[1.0625rem]">You’ve already used your free gilding.</p>
            <p className="font-display italic text-caption text-ink-soft">
              Sign in with your wallet to save it, share it, and keep gilding.
            </p>
            <ConnectButton
              label="Sign in to keep going"
              busy={connecting}
              onClick={() => void onConnected()}
            />
            {connectError && (
              <p className="font-display italic text-caption text-danger">{connectError}</p>
            )}
          </div>
        )}
        {freeMsg && (
          <div className="rounded-sm border border-danger/30 bg-danger/[0.05] px-4 py-3">
            <p className="font-display italic text-caption text-ink-soft">{freeMsg}</p>
          </div>
        )}

        <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-between">
          <p className="font-display italic text-caption text-ink-subtle">
            Free, no wallet. Sign in afterward to save &amp; make more.
          </p>
          {generateFreeMut.isPending ? (
            <GildingCeremony variant="sketch" className="w-full md:w-80" />
          ) : (
            <GildButton disabled={!canGild} onClick={() => generateFreeMut.mutate()}>
              Gild it, free
            </GildButton>
          )}
        </div>
      </div>
    );
  }

  // "Busy" spans the whole gild: the short start request, the background render
  // we're polling (jobId set), and any gallery recovery. The button stays locked
  // and the ceremony stays up across all three so a leaf can't be double-spent.
  const waiting = jobId !== null;
  const busy = generateMut.isPending || waiting || recovering;
  const disabled = (isRemix ? !isKontext : sketch.trim().length < 3) || busy;
  const errorMessage = (() => {
    // A failed background job already carries a humanised, leaf-safe message.
    if (jobError) return jobError;
    const e = generateMut.error;
    if (!(e instanceof Error)) return null;
    // A dropped socket isn't a failure — recovery is finding the saved gild.
    // Never show the red box for it (recovering) or after we've handed off to
    // the gentle gallery hint (recoverHint).
    if (isTransientNetworkError(e) && (recovering || recoverHint)) return null;
    const code = (e as { code?: string }).code ?? '';
    if (code === 'insufficient_credits' || e.message.includes('insufficient_credits')) {
      return 'You’re out of leaves. Each gilding spends a leaf or two.';
    }
    if (code === 'daily_cap_exceeded' || e.message.includes('daily_cap')) {
      return 'You’ve reached today’s gilding limit. Try again tomorrow.';
    }
    return e.message;
  })();
  const outOfLeaves = (() => {
    const e = generateMut.error;
    if (!(e instanceof Error)) return false;
    const code = (e as { code?: string }).code ?? '';
    return code === 'insufficient_credits' || e.message.includes('insufficient_credits');
  })();

  // Cinematic /new — the House of Gilding: six chambers scrubbed by scroll
  // (HouseScrub carries its own portrait/landscape assets and poster-first
  // fallback). Reduced-motion falls back to the classic editorial column
  // below, verbatim. `reduced` is declared above the early returns to keep
  // React's hook order stable across renders.
  // The cinematic House journey is for a fresh gild. A REMIX takes its scene
  // from the source piece, so walking the six chambers (threshold, sketch,
  // etc.) is noise — remix uses the clean, compact classic form below (source
  // preview + character + look/shape + gild). Reduced-motion also falls back.
  const cinematic = !reduced && !isRemix;

  if (cinematic) {
    return (
      <NewGenerationScrub
        isRemix={isRemix}
        remixSourceThumb={remixSourceThumb}
        isKontext={isKontext}
        characterName={characterName}
        showWhalePicker={showWhalePicker}
        whale={whale}
        onWhaleChange={(w) => {
          setWhale(w);
          if (w) setUploadedRef(null);
        }}
        uploadedRef={uploadedRef}
        onUploadedRefChange={(r) => {
          setUploadedRef(r);
          if (r) setWhale(null);
        }}
        sketch={sketch}
        onSketchChange={setSketch}
        suggestFlash={suggestFlash}
        onSurprise={() => suggestMut.mutate()}
        surprising={suggestMut.isPending}
        stylePreset={stylePreset}
        onStyleChange={setStylePreset}
        mode={mode}
        onModeChange={setMode}
        aspectRatio={aspectRatio}
        onAspectChange={setAspectRatio}
        onStrike={() => generateMut.mutate()}
        busy={busy}
        waiting={waiting}
        recovering={recovering}
        recoverHint={recoverHint}
        errorMessage={errorMessage}
        outOfLeaves={outOfLeaves}
        jobBrief={jobBrief}
        jobError={jobError}
      />
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-12 px-5 py-10 pb-32 md:py-14 md:pb-14">
      {/* ---------- Opening signature ---------- */}
      <Reveal as="header" className="flex flex-col gap-3">
        <h1 className="heading-display">
          {isRemix ? 'Remix this gilding' : isKontext ? `Send ${characterName ?? 'your character'} somewhere new` : 'Bring a sketch'}
        </h1>
        {!isRemix && !isKontext && (
          <p className="max-w-prose font-display italic text-lede text-ink-soft">
            One line, a feeling, a fragment of a scene. Claude takes it from there.
          </p>
        )}
        {isRemix && (
          <p className="max-w-prose font-display italic text-lede text-ink-soft">
            Pick your character. The scene comes from the source; the prompt is rewritten for
            your whale. The original creator&apos;s sketch is never shown.
          </p>
        )}
        {isKontext && !isRemix && (
          <p className="max-w-prose font-display italic text-lede text-ink-soft">
            Type a setting and we&apos;ll extend {characterName ?? 'your character'} into it.
          </p>
        )}
      </Reveal>

      {/* ---------- Remix source — typographic, no left-rail card ---------- */}
      {isRemix && remixSourceThumb && (
        <Reveal as="section" delay={60} className="flex items-start gap-5">
          <div className="card-plate flex-shrink-0">
            <img
              src={remixSourceThumb}
              alt="source gilding"
              className="block h-28 w-28 rounded-xs object-cover"
            />
          </div>
          <div className="flex flex-col pt-1">
            <span className="font-display italic text-caption text-ink-subtle">the source</span>
            <p className="mt-1 max-w-prose font-display text-lede italic text-ink-soft">
              The scene from this gilding will guide yours.
            </p>
          </div>
        </Reveal>
      )}

      {/* ---------- Character picker ---------- */}
      {showWhalePicker && (
        <Reveal delay={100}>
          <WhalePicker
            selected={whale}
            onChange={(w) => {
              setWhale(w);
              if (w) setUploadedRef(null);
            }}
          />
        </Reveal>
      )}

      {/* ---------- Any other character — by whale number or an upload ----------
          Lets a holder gild a character they don't own: another member's whale
          (great for welcomes), any NFT, or any uploaded image. Mutually exclusive
          with the owned-whale pick above. */}
      <Reveal as="section" delay={120} className="flex flex-col gap-2">
        <h2 className="heading-section">
          {showWhalePicker ? 'Or gild another character' : 'Add a character'}{' '}
          <span className="text-ink-subtle">(optional)</span>
        </h2>
        <p className="max-w-prose font-display italic text-caption text-ink-subtle">
          Look up any Whacky Whale by number, or upload an image of the character you want in the
          scene.
        </p>
        <FreeReferencePicker
          value={uploadedRef}
          onChange={(r) => {
            setUploadedRef(r);
            if (r) setWhale(null);
          }}
          uploadFn={api.uploadReference}
        />
      </Reveal>

      {/* ---------- The sketch — editorial textarea, no card chrome ---------- */}
      {!isRemix && (
        <Reveal delay={170}>
          <SketchSection
            isKontext={isKontext}
            whaleName={characterName}
            value={sketch}
            onChange={setSketch}
            max={SKETCH_MAX}
            onSurprise={() => suggestMut.mutate()}
            surprising={suggestMut.isPending}
            flashToken={suggestFlash}
            onExample={(text, style) => {
              setSketch(text);
              setStylePreset(style);
              setSuggestFlash((t) => t + 1);
            }}
          />
        </Reveal>
      )}

      {/* ---------- Style preset ---------- */}
      <Reveal as="section" delay={60} className="flex flex-col gap-4">
        <h2 className="heading-section">Pick the look</h2>
        <StylePicker value={stylePreset} onChange={setStylePreset} />
      </Reveal>

      {/* ---------- Mode + Aspect — paired editorial controls ---------- */}
      <Reveal as="section" delay={60} className="grid grid-cols-1 gap-10 md:grid-cols-12">
        <div className="flex flex-col gap-4 md:col-span-7">
          <h2 className="heading-section">How many images</h2>
          <ModePicker
            value={mode}
            onChange={setMode}
            disableBattle={isKontext}
          />
        </div>

        <div className="flex flex-col gap-4 md:col-span-5">
          <h2 className="heading-section">What shape</h2>
          <AspectPicker value={aspectRatio} onChange={setAspectRatio} />
        </div>
      </Reveal>

      {/* ---------- Backgrounded-gild recovery (gold, reassuring — not an error) ---------- */}
      {recovering && (
        <div className="rounded-sm border border-accent/30 bg-accent/[0.05] px-4 py-3">
          <p className="heading-section text-[1.0625rem]">Finishing your gilding…</p>
          <p className="mt-1 font-display italic text-caption text-ink-soft">
            Your screen slept mid-gild, but it kept working. Fetching the finished piece now.
          </p>
        </div>
      )}
      {recoverHint && !recovering && (
        <div className="rounded-sm border border-accent/30 bg-accent/[0.05] px-4 py-3">
          <p className="heading-section text-[1.0625rem]">Your gilding is still finishing</p>
          <p className="mt-1 font-display italic text-caption text-ink-soft">
            It’s saving to your gallery. Give it a moment, then take a look — no leaves were wasted.
          </p>
          <Link to="/gallery" className="btn-secondary mt-3 inline-flex">
            Open gallery
          </Link>
        </div>
      )}

      {/* ---------- Error ---------- */}
      {errorMessage && (
        <div className="rounded-sm border border-danger/30 bg-danger/[0.05] px-4 py-3">
          <p className="heading-section text-[1.0625rem] text-danger">
            {outOfLeaves ? 'Out of leaves' : 'Something didn’t take'}
          </p>
          <p className="mt-1 font-display italic text-caption text-ink-soft">{errorMessage}</p>
          {outOfLeaves && (
            <Link to="/buy" className="btn-secondary mt-3 inline-flex">
              Get more leaves
            </Link>
          )}
        </div>
      )}

      {/* ---------- The craft, while the render runs server-side ---------- */}
      {waiting && jobBrief?.amplified && (
        <Reveal className="w-full">
          <GildedBrief
            amplified={jobBrief.amplified}
            negative={jobBrief.negative}
            sketch={isKontext ? '' : sketch}
            title="What your line became"
            subtitle="Claude amplified your sketch into this brief while the image renders. It’ll appear the moment it’s ready, even if you leave this screen."
          />
        </Reveal>
      )}

      {/* ---------- Action ---------- */}
      <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-end">
        {busy && (
          <GildingCeremony
            variant={isRemix ? 'remix' : 'sketch'}
            className="w-full md:flex-1"
          />
        )}
        <GildButton
          busy={busy}
          busyLabel={recovering ? 'Finishing…' : waiting ? 'Rendering…' : 'Gilding…'}
          disabled={disabled}
          onClick={() => generateMut.mutate()}
        >
          {isRemix
            ? `Remix with ${characterName ?? 'my character'} (${leafCost(MODE_CREDITS[mode])})`
            : `Gild ${isKontext ? (characterName ?? 'your character') : 'it'} (${leafCost(MODE_CREDITS[mode])})`}
        </GildButton>
      </div>
    </div>
  );
}

/* ============================================================
   FreeResultView — the payoff of an anonymous free gilding: the
   image materialises out of gilt, then a single sign-in CTA.
   ============================================================ */

function FreeResultView({
  result,
  connecting,
  connectError,
  onConnect,
  onMakeYours,
}: {
  result: FreeResult;
  connecting: boolean;
  connectError: string | null;
  onConnect: () => void;
  onMakeYours: () => void;
}) {
  const img = result.images[0]?.url;
  const ratio = result.request.aspectRatio?.replace(':', ' / ');
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-8 px-5 py-10 md:py-14">
      <Reveal className="flex flex-col items-center gap-2 text-center">
        <span className="kicker">Your first gilding</span>
        <h1 className="heading-display">Struck from gold.</h1>
      </Reveal>

      <figure className="relative w-fit max-w-full">
        <div
          className="relative overflow-hidden rounded-md"
          style={{
            boxShadow: '0 30px 80px -24px rgb(0 0 0 / 0.7)',
            border: '1px solid rgb(255 255 255 / 0.06)',
            ...(ratio ? { aspectRatio: ratio } : {}),
          }}
        >
          {img && (
            <GildedImage
              src={img}
              alt="your gilding"
              className={
                ratio ? 'block max-h-[64vh] w-auto object-cover' : 'block max-h-[64vh] w-auto'
              }
            />
          )}
          {img && <GildUnveil key={result.id} src={img} delayMs={120} />}
        </div>
      </figure>

      {/* The craft, made visible — the one-liner gilded into a full brief. This
          is the "aha" that turns a free taste into a sign-up. */}
      {result.amplified?.amplified && (
        <Reveal delay={140} className="w-full max-w-2xl">
          <GildedBrief
            amplified={result.amplified.amplified}
            negative={result.amplified.negative}
            sketch={result.request.sketch}
            title="What your one line became"
            subtitle="Claude amplified your sketch into this brief: the craft behind every gilding."
          />
        </Reveal>
      )}

      <div className="flex w-full max-w-md flex-col items-center gap-4 text-center">
        <p className="font-display italic text-lede text-ink-soft">
          Sign in and pick up right here. Your sketch and style carry straight over, and
          everything you gild from now on is saved to your gallery.
        </p>
        <ConnectButton label="Make this yours" busy={connecting} onClick={onMakeYours} />
        <button
          type="button"
          onClick={onConnect}
          disabled={connecting}
          className="btn-ghost disabled:opacity-50"
        >
          or just sign in &amp; explore
        </button>
        {connectError && (
          <p className="font-display italic text-caption text-danger">{connectError}</p>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   SketchSection — heading + editorial textarea, properly labelled
   ============================================================ */

interface SketchSectionProps {
  isKontext: boolean;
  whaleName: string | null;
  value: string;
  onChange: (next: string) => void;
  max: number;
  onSurprise: () => void;
  surprising: boolean;
  /** Bumped by the parent when a suggestion lands — replays the underline flash. */
  flashToken: number;
  /** Fill the field from a curated example (+ switch to its look). */
  onExample: (text: string, style: StylePreset) => void;
}

function SketchSection({
  isKontext,
  whaleName,
  value,
  onChange,
  max,
  onSurprise,
  surprising,
  flashToken,
  onExample,
}: SketchSectionProps) {
  const fieldId = useId();
  const counterId = useId();
  // A fresh random handful of starting points per mount, so the chips never feel
  // like the same six ideas every visit. Stable for the component's lifetime.
  const examples = useMemo(() => pickRandom(SKETCH_EXAMPLES, EXAMPLE_COUNT), []);
  const placeholder = isKontext
    ? 'On the surface of the moon, earth rising, cinematic.'
    : 'An elder dragon emerging from a kelp forest, cinematic.';
  const label = isKontext ? `The scene for ${whaleName ?? 'your whale'}` : 'Your sketch';
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <label htmlFor={fieldId} className="heading-section">
          {label}
        </label>
        <button
          type="button"
          onClick={onSurprise}
          disabled={surprising}
          className="btn-ghost"
          aria-label="Generate a random scene suggestion"
        >
          <SurpriseDie spinning={surprising} />
          {surprising ? 'Rolling…' : 'Surprise me'}
        </button>
      </div>
      <div className="relative">
        <textarea
          id={fieldId}
          key={flashToken}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={max}
          rows={3}
          placeholder={placeholder}
          className={cn('field font-display text-lede', flashToken > 0 && 'field-flash')}
          spellCheck
          aria-describedby={counterId}
        />
        <div className="pointer-events-none absolute bottom-2 right-1">
          <CharacterRing length={value.length} max={max} />
        </div>
        <span id={counterId} aria-live="polite" className="sr-only">
          {value.length} of {max} characters used
        </span>
      </div>

      {/* Curated starting points — only while the field is empty, so they guide
          a first-timer without ever crowding a sketch in progress. */}
      <SceneIdeas character={isKontext ? whaleName ?? 'your character' : null} hasDraft={!!value.trim()} onUse={onExample} />
      {!isKontext && value.trim().length === 0 && (
        <div
          role="group"
          aria-label="Example sketches"
          className="flex flex-wrap items-baseline gap-x-4 gap-y-2"
        >
          <span className="font-display italic text-caption text-ink-subtle">Or start from:</span>
          {examples.map((ex) => (
            <button
              key={ex.text}
              type="button"
              onClick={() => onExample(ex.text, ex.style)}
              className="tag font-display italic text-caption text-ink-soft transition-colors duration-150 hover:text-ink"
              title={ex.text}
            >
              <span className="tag-dot" />
              {ex.label}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

/** Small d20-style die icon, spins while a suggestion is in flight. */
function SurpriseDie({ spinning }: { spinning: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={spinning ? 'motion-safe:animate-[spin_900ms_linear_infinite]' : ''}
      aria-hidden="true"
    >
      <path d="M8 1.5 L13.5 5 L13.5 11 L8 14.5 L2.5 11 L2.5 5 Z" />
      <path d="M8 1.5 L8 8 M13.5 5 L8 8 M2.5 5 L8 8 M8 8 L8 14.5 M8 8 L13.5 11 M8 8 L2.5 11" opacity="0.55" />
    </svg>
  );
}

interface CharacterRingProps {
  length: number;
  max: number;
}

function CharacterRing({ length, max }: CharacterRingProps) {
  const pct = Math.min(1, length / max);
  const radius = 7;
  const circumference = 2 * Math.PI * radius;
  const dash = circumference * pct;
  const warn = pct >= 0.9;
  const stroke = warn ? 'rgb(var(--color-danger))' : 'rgb(var(--color-accent))';

  return (
    <span
      className="group/ring pointer-events-auto inline-flex items-center justify-center"
      title={`${length} / ${max} characters`}
    >
      <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          stroke="rgb(var(--color-hairline))"
          strokeWidth="1.5"
        />
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference - dash}`}
          transform="rotate(-90 10 10)"
          style={{ transition: 'stroke-dasharray 200ms ease, stroke 200ms ease' }}
        />
      </svg>
      <span className="sr-only">
        {length} of {max} characters used
      </span>
    </span>
  );
}

/* ============================================================
   ModePicker — three tiles with bespoke SVG diagrams
   ============================================================ */

interface ModePickerProps {
  value: GenerationMode;
  onChange: (mode: GenerationMode) => void;
  disableBattle: boolean;
}

function ModePicker({ value, onChange, disableBattle }: ModePickerProps) {
  const modes: ReadonlyArray<GenerationMode> = ['single', 'double', '4up'];
  return (
    <ul
      role="radiogroup"
      aria-label="How many images"
      className="flex flex-col divide-y divide-hairline/60 border-y border-hairline/60"
    >
      {modes.map((m) => {
        const blocked = disableBattle && m === 'battle';
        const active = value === m;
        return (
          <li key={m}>
            <button
              type="button"
              role="radio"
              aria-checked={active}
              aria-disabled={blocked}
              disabled={blocked}
              onClick={() => !blocked && onChange(m)}
              className={cn(
                'group flex w-full items-center gap-4 px-1 py-4 text-left transition-colors duration-200 ease-page',
                blocked && 'cursor-not-allowed opacity-40',
              )}
            >
              {/* Left tick — thicker + gold when active. The radio affordance. */}
              <span
                aria-hidden="true"
                className={cn(
                  'inline-block h-6 w-px shrink-0 transition-[background,width] duration-200 ease-page',
                  active ? 'w-[3px] bg-accent' : 'bg-hairline-strong',
                )}
              />

              <ModeDiagram mode={m} active={active} />

              <div className="flex min-w-0 flex-1 flex-col">
                <span
                  className={cn(
                    'font-display text-[1.0625rem] transition-colors duration-200',
                    active ? 'font-semibold text-ink' : 'font-medium text-ink-soft',
                  )}
                >
                  {MODE_TITLE[m]}
                </span>
                <span className="mt-0.5 font-display italic text-caption text-ink-subtle">
                  {blocked ? 'Unavailable while a character is attached' : MODE_BLURB[m]}
                </span>
              </div>

              <span
                className={cn(
                  'flex-shrink-0 font-display text-caption italic',
                  active ? 'text-accent-deep' : 'text-ink-subtle',
                )}
              >
                {leafCost(MODE_CREDITS[m])}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ModeDiagram({ mode, active }: { mode: GenerationMode; active: boolean }) {
  const stroke = active ? 'rgb(var(--color-accent-deep))' : 'rgb(var(--color-ink-subtle))';
  const fill = active ? 'rgb(var(--color-accent) / 0.25)' : 'rgb(var(--color-hairline) / 0.4)';
  const common = {
    width: 44,
    height: 32,
    viewBox: '0 0 44 32',
    'aria-hidden': true as const,
  };
  switch (mode) {
    case 'single':
      // One image
      return (
        <svg {...common} className="flex-shrink-0">
          <rect x="13" y="3" width="18" height="26" fill={fill} stroke={stroke} strokeWidth="1" />
        </svg>
      );
    case 'double':
      // Two side-by-side images
      return (
        <svg {...common} className="flex-shrink-0">
          <rect x="2" y="3" width="18" height="26" fill={fill} stroke={stroke} strokeWidth="1" />
          <rect x="24" y="3" width="18" height="26" fill={fill} stroke={stroke} strokeWidth="1" />
          <circle cx="22" cy="16" r="1.4" fill={stroke} />
        </svg>
      );
    case '4up':
      // 2x2 grid
      return (
        <svg {...common} className="flex-shrink-0">
          <rect x="2" y="3" width="13" height="12" fill={fill} stroke={stroke} strokeWidth="1" />
          <rect x="17" y="3" width="13" height="12" fill={fill} stroke={stroke} strokeWidth="1" />
          <rect x="2" y="17" width="13" height="12" fill={fill} stroke={stroke} strokeWidth="1" />
          <rect x="17" y="17" width="13" height="12" fill={fill} stroke={stroke} strokeWidth="1" />
        </svg>
      );
    case 'battle':
      // Three vertical rectangles with 3 model glyph dots underneath
      return (
        <svg {...common} className="flex-shrink-0">
          <rect x="2" y="3" width="11" height="20" fill={fill} stroke={stroke} strokeWidth="1" />
          <rect x="16" y="3" width="11" height="20" fill={fill} stroke={stroke} strokeWidth="1" />
          <rect x="30" y="3" width="11" height="20" fill={fill} stroke={stroke} strokeWidth="1" />
          <g fill={stroke}>
            <circle cx="7.5" cy="27" r="1.4" />
            <circle cx="21.5" cy="27" r="1.4" />
            <circle cx="35.5" cy="27" r="1.4" />
          </g>
        </svg>
      );
  }
}

/* ============================================================
   AspectPicker — ratio swatches that ARE the ratio
   ============================================================ */

interface AspectPickerProps {
  value: AspectRatio;
  onChange: (next: AspectRatio) => void;
}

function AspectPicker({ value, onChange }: AspectPickerProps) {
  return (
    <div role="radiogroup" aria-label="Aspect ratio" className="grid grid-cols-5 gap-3">
      {ASPECT_RATIOS.map((a) => {
        const active = value === a.id;
        return (
          <button
            key={a.id}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={`${a.id} ${a.caption}`}
            onClick={() => onChange(a.id)}
            className={cn(
              'group flex min-h-[44px] flex-col items-center justify-end gap-2 rounded-sm py-3 transition-colors duration-200 ease-page',
            )}
          >
            {/* The control visually IS the ratio. No surrounding box — just
                the swatch + ring + label. */}
            <span
              className={cn(
                'block transition-[background,box-shadow] duration-200 ease-page',
                active
                  ? 'bg-accent/30 ring-2 ring-accent-deep'
                  : 'bg-hairline/40 ring-1 ring-hairline-strong/60 group-hover:bg-hairline-strong/40',
              )}
              style={{
                width: `${Math.max(a.w, 24)}px`,
                height: `${Math.max(a.h, 24)}px`,
              }}
            />
            <span
              className={cn(
                'font-display text-caption tracking-tight transition-colors duration-200',
                active ? 'font-semibold text-ink' : 'text-ink-subtle',
              )}
            >
              {a.id}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* Note: shared active-state ornaments (FoldedCorner) live in
   `components/FoldedCorner.tsx` — used by StylePicker only on this page.
   ModePicker uses a thicker left-tick, AspectPicker drops the surround
   and lets the swatch + ring speak. */
